/**
 * ブラウザの位置情報API（`navigator.geolocation`）を、自機位置送信用の
 * 形に変換する薄いラッパー。
 */

export interface GeoFix {
  t: number;
  lat: number;
  lon: number;
  altMsl: number | null;
  /** 対地速度 [m/s]。Geolocation API は元々 m/s */
  speed: number | null;
  heading: number | null;
  /** 水平精度 [m] */
  accuracy: number;
}

/** GeolocationPositionError のコード（1=権限拒否 2=取得不能 3=タイムアウト）を日本語にする */
function errorMessage(err: GeolocationPositionError): string {
  const messages: Record<number, string> = {
    1: '位置情報の利用が許可されていません（設定アプリで許可してください）',
    2: '位置情報を取得できません（電波状況などを確認してください）',
    3: '位置情報の取得がタイムアウトしました',
  };
  return messages[err.code] ?? err.message;
}

function toFix(pos: GeolocationPosition): GeoFix {
  return {
    t: pos.timestamp,
    lat: pos.coords.latitude,
    lon: pos.coords.longitude,
    altMsl: pos.coords.altitude,
    speed: pos.coords.speed,
    heading: pos.coords.heading,
    accuracy: pos.coords.accuracy,
  };
}

/**
 * 位置の継続取得を開始する。戻り値の関数を呼ぶと停止する。
 *
 * `navigator.geolocation` が無い環境（非対応ブラウザ、HTTP接続で
 * Secure Context でない場合など）では即座にエラーを通知し、no-op の
 * 停止関数を返す。
 */
export function watchOwnPosition(
  onFix: (fix: GeoFix) => void,
  onError: (message: string) => void,
): () => void {
  if (!('geolocation' in navigator)) {
    onError('この端末・ブラウザは位置情報に対応していません');
    return () => {};
  }

  const id = navigator.geolocation.watchPosition(
    (pos) => onFix(toFix(pos)),
    (err) => onError(errorMessage(err)),
    { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 },
  );

  return () => navigator.geolocation.clearWatch(id);
}

/**
 * 一度だけ現在地を取得する。送信中かどうかに関わらず使える「自分の位置に
 * 地図を移動」ボタン用（継続取得はしない、その場限りの一回読み）。
 */
export function getOwnPositionOnce(): Promise<GeoFix> {
  return new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      reject(new Error('この端末・ブラウザは位置情報に対応していません'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(toFix(pos)),
      (err) => reject(new Error(errorMessage(err))),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  });
}
